import _ from "lodash"

/**
 * 使用 8 位电话号码的区号
 * @type {string[]}
 */
const LIMIT_8 = [
  "010",
  "020",
  "021",
  "022",
  "023",
  "024",
  "025",
  "026",
  "027",
  "028",
  "029",
  "0311",
  "0371",
  "0377",
  "0379",
  "0411",
  "0417",
  "0431",
  "0432",
  "0451",
  "0510",
  "0511",
  "0512",
  "0513",
  "0514",
  "0515",
  "0516",
  "0517",
  "0518",
  "0519",
  "0523",
  "0527",
  "0531",
  "0532",
  "0551",
  "0558",
  "0571",
  "0573",
  "0574",
  "0575",
  "0576",
  "0577",
  "0579",
  "0591",
  "0595",
  "0731",
  "0754",
  "0755",
  "0757",
  "0760",
  "0769",
  "0791",
  "0851",
  "0871",
  "0898",
]

class Phone {
  constructor(countryCode, cityCode, telephone, extension, mobile, special, error, number) {
    countryCode && (this.countryCode = countryCode)
    cityCode && (this.cityCode = cityCode)
    telephone && (this.telephone = telephone)
    extension && (this.extension = extension)
    mobile && (this.mobile = mobile)
    special && (this.special = special)
    error && (this.error = error)
    number && (this.number = number)
  }

  /**
   * 是否手机号码
   * @return {boolean}
   */
  get isMobile() {
    return !!this.mobile
  }

  /**
   * 是否固话
   * @return {boolean}
   */
  get isTelephone() {
    return !!this.telephone || this.isSpecial
  }

  /**
   * 是否特殊号码
   * @return {boolean}
   */
  get isSpecial() {
    return !!this.special
  }

  /**
   * 是否国际长途
   * @return {*|boolean}
   */
  get isInternational() {
    return this.countryCode && this.countryCode !== "86"
  }

  format() {
    if (this.mobile) {
      return this.mobile
    } else if (this.special) {
      return this.special
    } else {
      if (this.isInternational) {
        return "+" + this.countryCode + this.telephone + (this.extension ? "-" + this.extension : "")
      } else {
        return this.cityCode + "-" + this.telephone + (this.extension ? "-" + this.extension : "")
      }
    }
  }

  toString() {
    return this.format()
  }
}

class PhoneParse {
  constructor(number) {
    if (!number) {
      return
    }

    this.number = number
    this.index = 0

    try {
      let char = this.peek()
      // +........
      if (char === "+") {
        this.skip()
        this.parseFullTelephone()
      }
      // (..).....
      else if (char === "(") {
        char = this.peek(1)
        if (char === "0") {
          this.skip(2)
          this.cityCode = this.parseCityCode()
        } else {
          this.skip(1)
          this.countryCode = this.parseCountryCode()
          this.cityCode = this.parseCityCode()
        }
        char = this.next(1)
        if (char === ")") {
          this.telephone = this.parseTelephone()
          this.extension = this.parseExtension()
        } else {
          this.error = "号码格式不正确"
        }
      }
      // 0........
      else if (char === "0") {
        this.skip()
        char = this.peek()
        // 00........
        if (char === "0") {
          this.skip()
          this.parseFullTelephone()
        }
        // 0xxx......
        else {
          this.parseCityTelephone()
        }
      }
      // 1.......
      else if (char === "1") {
        if (this.number.length === 3) {
          this.special = this.nextDigital(3)
        } else if (this.number.length > 4 && this.number.length < 11) {
          const four = this.number.substr(0, 4)
          if (four === "1001" || four === "1010") {
            this.special = this.nextDigital(this.number.length)
          }
        } else {
          this.mobile = this.parseMobile()
        }
      }
      // x.......
      else {
        const two = this.number.substr(0, 2)
        const three = this.number.substr(0, 3)
        if (two === "95" && this.number.length >= 5) {
          this.special = this.nextDigital(this.number.length)
        } else if ((three === "400" || three === "800") && this.number.length >= 10) {
          let s1 = this.nextDigital(3)
          this.skipDash()
          let s2 = this.nextDigital(3)
          this.skipDash()
          let s3 = this.nextDigital(4)
          this.special = s1.concat(s2).concat(s3)
        } else {
          this.error = "号码格式不正确"
        }
      }
      // 判断是否过长
      if (!this.error && this.index < this.number.length) {
        if (this.mobile || this.telephone || this.special) {
          this.error = "号码输入过长"
        } else {
          this.error = "号码输入过短"
        }
      }
    } catch (e) {
      this.error = e.message
    }
  }

  /**
   * 获取解析结果
   * @return {Phone}
   */
  get() {
    return new Phone(this.countryCode, this.cityCode, this.telephone, this.extension, this.mobile, this.special, this.error, this.number)
  }

  /**
   * 解析完整电话号码
   */
  parseFullTelephone() {
    this.countryCode = this.parseCountryCode()
    if (this.countryCode === "86") {
      this.parseCityTelephone()
    } else if (this.countryCode === "1") {
      this.telephone = this.nextDigital(10)
      this.extension = this.parseExtension()
    } else {
      // 其他国家/地区的电话号码
      this.telephone = this.restDigital()
    }
  }

  /**
   * 解析不含国家编码的电话号码
   */
  parseCityTelephone() {
    this.skipDash()
    const index = this.peek() === "0" ? 1 : 0
    if (this.peek(index) === "1" && this.peek(index + 1) !== "0") {
      this.mobile = this.parseMobile()
    } else {
      this.cityCode = this.parseCityCode()
      this.telephone = this.parseTelephone()
      this.extension = this.parseExtension()
    }
  }

  /**
   * 国际电话区号列表
   * https://zh.wikipedia.org/wiki/%E5%9B%BD%E9%99%85%E7%94%B5%E8%AF%9D%E5%8C%BA%E5%8F%B7%E5%88%97%E8%A1%A8
   */
  parseCountryCode() {
    const char = this.peek()
    // 北美
    if (char === "1") {
      return this.nextDigital(1)
    }
    // 非洲
    else if (char === "2") {
      const second = this.peek(1)
      if (second === "0" || second === "7" || second === "8") {
        return this.nextDigital(2)
      } else {
        return this.nextDigital(3)
      }
    }
    // 欧洲
    else if (char === "3") {
      const second = this.peek(1)
      if (second === "5" || second === "7" || second === "8") {
        return this.nextDigital(3)
      } else {
        return this.nextDigital(2)
      }
    }
    // 欧洲
    else if (char === "4") {
      const second = this.peek(1)
      if (second === "2") {
        return this.nextDigital(3)
      } else {
        return this.nextDigital(2)
      }
    }
    // 墨西哥及中南美洲
    else if (char === "5") {
      const second = this.peek(1)
      if (second === "0" || second === "9") {
        return this.nextDigital(3)
      } else {
        return this.nextDigital(2)
      }
    }
    // 东南亚及大洋洲
    else if (char === "6") {
      const second = this.peek(1)
      if (second === "7" || second === "8" || second === "9") {
        return this.nextDigital(3)
      } else {
        return this.nextDigital(2)
      }
    }
    // 前苏联地区
    else if (char === "7") {
      return this.nextDigital(2)
    }
    // 东亚及特殊服务
    else if (char === "8") {
      const second = this.peek(1)
      if (second === "0" || second === "5" || second === "7" || second === "8" || second === "9") {
        return this.nextDigital(3)
      } else {
        return this.nextDigital(2)
      }
    }
    // 西亚及南亚、中东
    else if (char === "9") {
      const second = this.peek(1)
      if (second === "6" || second === "7" || second === "9") {
        return this.nextDigital(3)
      } else {
        return this.nextDigital(2)
      }
    } else {
      throw new Error("号码格式不正确")
    }
  }

  /**
   * 城市区号
   * https://zh.wikipedia.org/wiki/%E4%B8%AD%E5%9B%BD%E5%A4%A7%E9%99%86%E7%94%B5%E8%AF%9D%E5%8C%BA%E5%8F%B7
   * @return {string}
   */
  parseCityCode() {
    this.skipDash()
    const char = this.peek()
    // 01..
    if (char === "1") {
      const second = this.peek(1)
      // 010
      if (second === "0") {
        return "0" + this.nextDigital(2)
      }
      // 01x
      else {
        throw new Error("区号不存在")
      }
    }
    // 02..
    else if (char === "2") {
      return "0" + this.nextDigital(2)
    }
    // 0[3-9]..
    else {
      return "0" + this.nextDigital(3)
    }
  }

  /**
   * 固话总机
   * @return {string}
   */
  parseTelephone() {
    this.skipDash()
    const limit = _.includes(LIMIT_8, this.cityCode) ? 8 : 7
    const result = this.nextDigital(limit)
    if (result[0] === "0") {
      throw new Error("总机号码非法")
    }
    return result
  }

  /**
   * 固话分机
   * @return {string}
   */
  parseExtension() {
    this.skipDash()
    const result = this.restDigital()
    if (result) {
      if (result.length < 3) {
        throw new Error("分机号码过短")
      } else if (result.length > 5) {
        throw new Error("分机号码过长")
      }
    }
    return result
  }

  /**
   * 手机
   * https://zh.wikipedia.org/wiki/%E4%B8%AD%E5%9B%BD%E5%86%85%E5%9C%B0%E7%A7%BB%E5%8A%A8%E7%BB%88%E7%AB%AF%E9%80%9A%E8%AE%AF%E5%8F%B7%E6%AE%B5
   * @return {string}
   */
  parseMobile() {
    this.skipDash()
    return this.nextDigital(11)
  }

  /**
   * 提取指定位置的字符
   * @param pos
   * @return {*}
   */
  peek(pos = 0) {
    return this.number[this.index + pos]
  }

  /**
   * 跳过指定长度
   * @param length
   */
  skip(length = 1) {
    if (this.index + length > this.number.length) {
      throw new Error("号码输入过短")
    }
    this.index += length
  }

  /**
   * 跳过分隔符
   */
  skipDash() {
    const char = this.peek()
    if (char === "-") {
      this.skip()
    }
  }

  /**
   * 获取下一段
   * @param length
   * @return {string}
   */
  next(length) {
    if (length > 0) {
      if (this.index + length > this.number.length) {
        throw new Error("号码输入过短")
      }

      const token = this.number.substr(this.index, length)
      this.index += length
      return token
    } else {
      return undefined
    }
  }

  /**
   * 获取下一批数字
   * @param length
   * @return {string}
   */
  nextDigital(length) {
    const token = this.next(length)
    if (/^\d+$/.test(token)) {
      return token
    } else {
      throw new Error("号码输入格式不正确")
    }
  }

  /**
   * 获取剩余数字
   * @return {string|undefined}
   */
  restDigital() {
    if (this.index >= this.number.length) {
      return undefined
    } else {
      return this.nextDigital(this.number.length - this.index)
    }
  }
}

export default function parsePhone(number) {
  if (number instanceof Phone) {
    return number
  }
  return new PhoneParse(number).get()
}
